import { useNavigate } from 'react-router-dom'

import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'

import { ButtonSize, ButtonTheme } from 'builder/components/Button'
import { useQueryParam } from 'builder/hooks/useQueryParam'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, ColumnName, selectors as jtSelectors } from 'builder/modules/jobTracking'
import { JoyRidesJobSearchTrackingSteps } from 'builder/components/FindJob/JoyRidesJobSearch/JoyRidesJobSearchTrackingSteps'
import { useGetJob } from 'builder/components/FindJob/JobAlertPage/hooks/useGetJob'
import { Routes, StartCompleteScreenRoute } from '../../constants'
import { useAutoApply } from '../../hooks/useAutoApply'
import {
  Container,
  InfoHeader,
  InfoIcon,
  InfoHeadContiner,
  InfoSubHeader,
  InfoActions,
  InfoActionIcon,
  InfoActionDescription,
  CtaContainer,
  InfoActionDescriptionBold,
  StartButton,
  InfoBoxContainer,
  InfoActionContainer,
  HorizontalDivider,
  InfoBox,
  InfoBoxElement,
} from './styles'
import starImage from './assets/star-image.png'
import { VideoPlayer } from './VideoPlayer'

const TRANSLATION = `builder.auto_apply.start_screen`

function usePageTranslations(userHasCompletedQuestionnaire?: boolean) {
  const { i18n } = useI18n()

  return {
    headerText: userHasCompletedQuestionnaire
      ? i18n.t(`${TRANSLATION}.second_header`)
      : i18n.t(`${TRANSLATION}.header`),
    subHeaderText: userHasCompletedQuestionnaire
      ? i18n.t(`${TRANSLATION}.second_sub_header`)
      : i18n.t(`${TRANSLATION}.sub_header`),
    startCtaText: userHasCompletedQuestionnaire
      ? i18n.t(`${TRANSLATION}.edit_start_text`)
      : i18n.t(`${TRANSLATION}.start_text`),
    exploreJobsText: i18n.t(`${TRANSLATION}.explore_jobs`),
  }
}

export const StartScreenView = () => {
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isPhone, isTablet } = useMediaQueries()
  const cards = useTypedSelector(jtSelectors.savedJobs)
  const [jobId] = useQueryParam('job_id')
  const job = useGetJob(jobId || undefined)
  const autoApply = useAutoApply()
  const userHasCompletedQuestionnaire = autoApply.hasUserCompletedQuestionnaire
  const { headerText, subHeaderText, startCtaText, exploreJobsText } = usePageTranslations(
    userHasCompletedQuestionnaire,
  )
  const isTabletOrMobile = isPhone || isTablet

  const onStart = () => {
    if (userHasCompletedQuestionnaire) {
      navigate(`/auto-apply/${Routes.TAILOR_RESUME}`)
    } else {
      navigate(`/auto-apply/${StartCompleteScreenRoute.RESUME_SELECT}`)
    }
  }

  useEffect(() => {
    trackInternalEvent('enter_welcome_screen', {
      label: 'total_job_search_solution',
    })
  }, [])

  useEffect(() => {
    if (
      !job.isLoading &&
      job.data &&
      !cards.find(el => el.externalSlugId === job.data.external_slug_id)
    ) {
      const {
        company,
        url,
        description,
        external_slug_id: externalSlugId,
        job_title: jobTitle,
        location,
        company_logo: logo,
      } = job.data
      dispatch(
        actions.createJobRequest({
          status: ColumnName.wishlist,
          title: jobTitle,
          company,
          link: url,
          description,
          external_slug_id: externalSlugId,
          location,
          logo,
          dummy: false,
        }),
      )
    }
  }, [cards.length, job.data])

  const CallToActions = (
    <CtaContainer>
      <StartButton
        theme={userHasCompletedQuestionnaire ? ButtonTheme.secondary : ButtonTheme.default}
        size={isPhone ? ButtonSize.small : ButtonSize.default}
        onClick={onStart}
      >
        {startCtaText}
      </StartButton>
      {userHasCompletedQuestionnaire && (
        <StartButton
          onClick={() => navigate('/job-search?view=recommendation')}
          size={isPhone ? ButtonSize.small : ButtonSize.default}
        >
          {exploreJobsText}
        </StartButton>
      )}
    </CtaContainer>
  )

  const InfoActionComponent = (
    <InfoActions>
      <InfoActionDescription hidden={userHasCompletedQuestionnaire}>
        <InfoActionIcon />
        {i18n.t(`${TRANSLATION}.time`)}
        <InfoActionDescriptionBold>
          {i18n.t(`${TRANSLATION}.time_minutes`)}
        </InfoActionDescriptionBold>
      </InfoActionDescription>
      {isTabletOrMobile && CallToActions}
    </InfoActions>
  )

  return (
    <Container>
      <JoyRidesJobSearchTrackingSteps />
      <InfoBox>
        <InfoBoxContainer>
          <InfoBoxElement>
            <InfoIcon $width={isPhone ? 42 : 72} $height={isPhone ? 48 : 83} src={starImage} />
            <InfoHeadContiner>
              <InfoHeader>{headerText}</InfoHeader>
              <InfoSubHeader>{subHeaderText}</InfoSubHeader>

              {!isTabletOrMobile && CallToActions}
            </InfoHeadContiner>
          </InfoBoxElement>

          <InfoBoxElement>
            <VideoPlayer />
          </InfoBoxElement>
        </InfoBoxContainer>

        <InfoActionContainer>
          <HorizontalDivider hidden={userHasCompletedQuestionnaire || isTabletOrMobile} />
          {InfoActionComponent}
        </InfoActionContainer>
      </InfoBox>
    </Container>
  )
}
